import type { ColumnConfiguration, ListBuilderConfiguration } from '../types';

/**
 * PD-17504 — every other name an item goes by: a customer's own item number
 * (`client_item`) or a competitor, maker or standard number
 * (`item_cross_reference`), as one list.
 *
 * Two lists share these columns:
 * - `item-cross-references-embedded`, the item's Cross-References tab, from
 *   GET /api/items/:itemId/cross-references, with a remove button per row;
 * - `item-cross-references`, the Cross-References list under Items, from
 *   GET /api/item-cross-references, which adds our item and is read-only
 *   (changes are made on the item's tab).
 * Both page, search, filter and sort on the server. Sort field names are the
 * server's whitelist keys (ITEM_CROSS_REFERENCE_SORT_COLUMNS).
 */
const TABLE = 'item_cross_reference';

/** The kinds as chips. Values are `ItemCrossReferenceRowKind`. */
const KIND_VALUE_MAPPING = [
	{ value: 'client_item_number', label: 'Client item number', color: { type: 'info' } },
	// A categorical hue (chip palette 4, purple): a peer of the others, not a status.
	{ value: 'maker_or_competitor', label: 'Maker or competitor', color: { category: 3 } },
	{ value: 'standard', label: 'Standard', color: { type: 'neutral' } },
];

const sortOn = (fieldName: string, direction: 'ASC' | 'DESC' = 'ASC'): ColumnConfiguration['sortFields'] => [
	{ objectTable: TABLE, fieldName, direction },
];

const theirNumberColumn = (): ColumnConfiguration => ({
	id: 'xref-number',
	fields: [
		{
			id: 'xref-number-value',
			dataConfig: { type: 'field', field: { fieldName: 'number', objectTable: TABLE } },
			styleConfig: { size: 'md', weight: 'medium', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-xref-number', text: 'Their number' }],
	minWidth: 140,
	sortable: true,
	sortFields: sortOn('number'),
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

const ourItemColumn = (): ColumnConfiguration => ({
	id: 'xref-our-item',
	fields: [
		{
			id: 'xref-our-item-description',
			link: { type: 'object', enabled: true, objectTable: 'item', idField: 'itemId' },
			dataConfig: {
				type: 'field',
				field: { fieldName: 'itemDescription', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{
			id: 'xref-our-item-lb',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
			conditionalVisibility: { field: 'itemCode', condition: 'is_not_null' },
		},
		{
			id: 'xref-our-item-code',
			dataConfig: { type: 'field', field: { fieldName: 'itemCode', objectTable: TABLE } },
			styleConfig: { size: 'xs', colorRule: { type: 'muted' } },
			displayStyle: 'text',
			conditionalVisibility: { field: 'itemCode', condition: 'is_not_null' },
		},
	],
	headers: [{ id: 'h-xref-our-item', text: 'Our item' }],
	minWidth: 220,
	sortable: true,
	sortFields: sortOn('itemDescription'),
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

const kindColumn = (): ColumnConfiguration => ({
	id: 'xref-kind',
	fields: [
		{
			id: 'xref-kind-value',
			dataConfig: { type: 'field', field: { fieldName: 'kind', objectTable: TABLE } },
			styleConfig: { size: 'sm', valueMapping: KIND_VALUE_MAPPING },
			displayStyle: 'badge',
		},
	],
	headers: [{ id: 'h-xref-kind', text: 'Kind' }],
	minWidth: 160,
	sortable: true,
	sortFields: sortOn('kind'),
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

const whoseColumn = (): ColumnConfiguration => ({
	id: 'xref-whose',
	fields: [
		{
			// A customer's own number links to the customer.
			id: 'xref-whose-client',
			link: { type: 'object', enabled: true, objectTable: 'client', idField: 'clientId' },
			dataConfig: { type: 'field', field: { fieldName: 'whose', objectTable: TABLE } },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
			conditionalVisibility: { field: 'clientId', condition: 'is_not_null' },
		},
		{
			// "Any customer": no customer to link to.
			id: 'xref-whose-any',
			dataConfig: { type: 'field', field: { fieldName: 'whose', objectTable: TABLE } },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
			conditionalVisibility: { field: 'clientId', condition: 'is_null' },
		},
	],
	headers: [{ id: 'h-xref-whose', text: 'Whose' }],
	minWidth: 160,
	sortable: true,
	sortFields: sortOn('whose'),
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

const howWeKnowColumn = (): ColumnConfiguration => ({
	id: 'xref-how',
	fields: [
		{
			id: 'xref-how-value',
			dataConfig: { type: 'field', field: { fieldName: 'howWeKnow', objectTable: TABLE } },
			styleConfig: { size: 'md', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
		{
			id: 'xref-how-lb',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
			conditionalVisibility: { field: 'quoteRequestId', condition: 'is_not_null' },
		},
		{
			id: 'xref-how-request',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'cross_reference_request',
				idField: 'quoteRequestId',
			},
			dataConfig: { type: 'custom_text', customText: 'Open quote request' },
			styleConfig: { size: 'xs', colorRule: { type: 'default' } },
			displayStyle: 'text',
			conditionalVisibility: { field: 'quoteRequestId', condition: 'is_not_null' },
		},
	],
	headers: [{ id: 'h-xref-how', text: 'How we know' }],
	minWidth: 200,
	sortable: true,
	sortFields: sortOn('howWeKnow'),
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

const addedColumn = (): ColumnConfiguration => ({
	id: 'xref-added',
	fields: [
		{
			id: 'xref-added-at',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'addedAt', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
				formatOptions: { dateFormat: 'short' },
			},
			displayStyle: 'date',
		},
	],
	headers: [{ id: 'h-xref-added', text: 'Added' }],
	minWidth: 110,
	sortable: true,
	sortFields: sortOn('addedAt', 'DESC'),
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

const removeColumn = (): ColumnConfiguration => ({
	id: 'xref-remove',
	fields: [
		{
			// Shown only when the caller holds the permission that removes this
			// row's kind (`canRemove`, computed by the server per row): a
			// customer's number needs clients:item_numbers:manage, an outside
			// number item_matching:cross_references:manage.
			id: 'xref-remove-button',
			dataConfig: { type: 'custom_text', customText: 'Remove' },
			styleConfig: {
				size: 'sm',
				iconName: 'xmark',
				variant: 'outline',
				isCircular: true,
				colorRule: { type: 'default' },
			},
			displayStyle: 'button',
			link: { enabled: true, type: 'callback', callbackName: 'removeCrossReference' },
			conditionalVisibility: { field: 'canRemove', condition: 'equals', value: true },
		},
	],
	headers: [{ id: 'h-xref-remove', text: '' }],
	width: 64,
	sortable: false,
	alignment: 'right',
});

const OBJECTS: NonNullable<ListBuilderConfiguration['objects']> = [
	{
		objectLabel: 'Cross-Reference',
		objectTable: TABLE,
		// Rows come from two tables, so the key is `<origin>-<id>`.
		rowIdField: 'rowKey',
		selectedFields: [
			{ fieldName: 'number', fieldLabel: 'Their number' },
			{ fieldName: 'kind', fieldLabel: 'Kind' },
			{ fieldName: 'whose', fieldLabel: 'Whose' },
			{ fieldName: 'howWeKnow', fieldLabel: 'How we know' },
			{ fieldName: 'addedAt', fieldLabel: 'Added' },
			{ fieldName: 'itemDescription', fieldLabel: 'Our item' },
			{ fieldName: 'itemCode', fieldLabel: 'Our item code' },
		],
	},
	{
		baseUrl: '/items/:id',
		objectLabel: 'Item',
		objectTable: 'item',
		selectedFields: [{ fieldName: 'itemId', fieldLabel: 'ID' }],
	},
	{
		baseUrl: '/clients/:id',
		objectLabel: 'Client',
		objectTable: 'client',
		selectedFields: [{ fieldName: 'clientId', fieldLabel: 'ID' }],
	},
	{
		baseUrl: '/quote-requests/:id',
		objectLabel: 'Quote Request',
		objectTable: 'cross_reference_request',
		selectedFields: [{ fieldName: 'quoteRequestId', fieldLabel: 'ID' }],
	},
];

/** The item's Cross-References tab. */
export const ITEM_CROSS_REFERENCES_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-item-cross-references-embedded',
			cacheTTL: 0,
			name: 'Cross-References',
			type: 'default',
			active: true,
			sortOrder: 0,
			defaultSortId: 'column-xref-kind-asc',
			columns: [
				theirNumberColumn(),
				kindColumn(),
				whoseColumn(),
				howWeKnowColumn(),
				addedColumn(),
				removeColumn(),
			],
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100],
	},
	// No filters: one item's names fit on a page, and the tab shows the list
	// without its header (the design puts the intro and buttons there).
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: OBJECTS,
};

/** The Cross-References list under Items. */
export const ITEM_CROSS_REFERENCES_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-item-cross-references',
			cacheTTL: 0,
			name: 'All Cross-References',
			type: 'default',
			active: true,
			sortOrder: 0,
			defaultSortId: 'column-xref-number-asc',
			columns: [
				theirNumberColumn(),
				ourItemColumn(),
				kindColumn(),
				whoseColumn(),
				howWeKnowColumn(),
				addedColumn(),
			],
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['itemCrossReferenceKeywords', 'itemCrossReferenceKind'],
		defaultFilters: {},
	},
	objects: OBJECTS,
};
